import type { AtlasEvaluation } from '../types/evaluation';
import { ScaleColumn } from './scale-ladder/ScaleColumn';
import { ScaleColumnFooter } from './scale-ladder/ScaleColumnFooter';
import { ScaleLevelBox } from './scale-ladder/ScaleLevelBox';
import {
  getLevelState,
  getLevelVariant,
  LADDER_ALIGN_SCORES,
  SCALE_LEVELS,
} from '../utils/scaleLevels';
import type { ScaleAcronym } from '../utils/scoreColor';

interface ScoreTriptychProps {
  evaluation: AtlasEvaluation;
}

interface ColumnDef {
  acronym: ScaleAcronym;
  primaryName: 'Truth' | 'Engagement' | 'Integration';
  fullName: string;
  score: number;
  justification: string;
  emphasized?: boolean;
}

/**
 * Base scores as ATLASSYSTEM8-style column ladders.
 * Desktop: shared grid so zeros align (ETS +4 raised above).
 * Mobile: stacked ETS first, then SES, EIS.
 */
export function ScoreTriptych({ evaluation }: ScoreTriptychProps) {
  const { ets, ses, eis } = evaluation;

  const columns: ColumnDef[] = [
    {
      acronym: 'SES',
      primaryName: 'Engagement',
      fullName: 'Scientific Engagement Scale',
      score: ses.score,
      justification: ses.justification,
    },
    {
      acronym: 'ETS',
      primaryName: 'Truth',
      fullName: 'Established Truth Scale',
      score: ets.score,
      justification: ets.justification,
      emphasized: true,
    },
    {
      acronym: 'EIS',
      primaryName: 'Integration',
      fullName: 'Explanatory Integration Scale',
      score: eis.score,
      justification: eis.justification,
    },
  ];

  return (
    <div className="w-full">
      {/* -- Mobile: stacked columns, ETS (primary) first */}
      <div className="flex flex-col gap-6 md:hidden">
        <ScaleColumn
          acronym="ETS"
          primaryName="Truth"
          fullName="Established Truth Scale"
          score={ets.score}
          justification={ets.justification}
          emphasized
          orderClassName=""
          alignRows={false}
        />
        <ScaleColumn
          acronym="SES"
          primaryName="Engagement"
          fullName="Scientific Engagement Scale"
          score={ses.score}
          justification={ses.justification}
          orderClassName=""
          alignRows={false}
        />
        <ScaleColumn
          acronym="EIS"
          primaryName="Integration"
          fullName="Explanatory Integration Scale"
          score={eis.score}
          justification={eis.justification}
          orderClassName=""
          alignRows={false}
        />
      </div>

      {/* -- Desktop: one grid so each score value shares a horizontal row */}
      <div className="hidden md:grid md:grid-cols-3 md:items-stretch md:gap-x-3 md:gap-y-1.5 lg:gap-x-4">
        {LADDER_ALIGN_SCORES.flatMap((levelScore) =>
          columns.map((col) => {
            const level = SCALE_LEVELS[col.acronym].find(
              (l) => l.score === levelScore
            );
            if (!level) {
              return (
                <div
                  key={`${col.acronym}-pad-${levelScore}`}
                  aria-hidden="true"
                  className="min-h-[2.75rem]"
                />
              );
            }

            const state = getLevelState(col.score, level.score);
            return (
              <ScaleLevelBox
                key={`${col.acronym}-${level.score}`}
                score={level.score}
                label={level.label}
                state={state}
                variant={getLevelVariant(col.acronym, level.score)}
                negativeMode={col.score < 0}
                justification={
                  state === 'active' ? col.justification : undefined
                }
                className="h-full"
              />
            );
          })
        )}

        {columns.map((col) => (
          <ScaleColumnFooter
            key={`${col.acronym}-footer`}
            acronym={col.acronym}
            primaryName={col.primaryName}
            fullName={col.fullName}
            score={col.score}
            emphasized={col.emphasized}
          />
        ))}
      </div>
    </div>
  );
}
